import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ReactComponent as ArrowRightIcon } from '@pkg/assets/icons/v2/arrow-right-thick.svg';

import { useLegacyTooltip } from '@app/pages/Dashboard/useLegacyTooltip';

import Tag, { type TagProps } from '../Tag';

import './Top.scss';

export interface TopCardProps {
    bgImageClassName?: string;
    disabled?: boolean;
    headline: string;
    onClick?: VoidFunction;
    tag?: string | null;
    seeMore?: boolean;
    size?: 'single' | 'double';
    status?: TagProps['status'];
    subHeadline?: string;
    pageTitle?: string;
    videoUrl?: string;
}

export const TopCard = (props: TopCardProps) => {
    const { bgImageClassName, disabled, headline, onClick, seeMore, size, status, subHeadline, tag, videoUrl } = props;
    const [canPlay, setCanPlay] = useState(false);
    const videoBackgroundRef = useRef<HTMLVideoElement>(null);
    const videoPlayControl = useRef({});
    const { LegacyTooltipWrapper } = useLegacyTooltip();

    let tagElement;

    if (tag) {
        tagElement = <Tag label={tag} {...(status ? { status } : null)} />;
    }

    const handlePlay = useCallback(() => {
        const video = videoBackgroundRef.current;
        if (video && canPlay) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        return video.play();
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                        return video.pause();
                    });
            }
        }
    }, [canPlay]);

    useLayoutEffect(() => {
        const video = videoBackgroundRef?.current;

        if (video) {
            const isPlaying = Boolean(
                video &&
                    video.currentTime > 0 &&
                    !video.paused &&
                    !video.ended &&
                    video.readyState > video.HAVE_CURRENT_DATA,
            );

            setCanPlay(!isPlaying);
        }
    }, []);

    const containerClasses = ['yat-top-card', `yat-top-card--${size}`];
    if (disabled) {
        containerClasses.push('yat-top-card--disabled');
    }
    const cardBg = useMemo(() => {
        if (videoUrl) {
            videoPlayControl.current = {
                onMouseEnter: () => handlePlay(),
                onTouchStart: () => handlePlay(),
                onMouseLeave: () => videoBackgroundRef.current?.pause(),
            };
            return (
                <video
                    muted
                    webkit-playsinline="true"
                    onCanPlay={() => setCanPlay(true)}
                    playsInline
                    className={`yat-top-card__video ${
                        bgImageClassName ? `yat-top-card__image ${bgImageClassName}` : ''
                    }`}
                    ref={videoBackgroundRef}
                >
                    <source src={videoUrl} type="video/mp4" />
                </video>
            );
        } else {
            return <div className={`yat-top-card__image ${bgImageClassName ? bgImageClassName : ''}`} />;
        }
    }, [videoUrl, bgImageClassName, handlePlay]);

    useEffect(() => {
        videoBackgroundRef?.current?.load();
    }, [videoUrl]);

    const comp = (
        <div className={containerClasses.join(' ')} onClick={onClick} {...videoPlayControl.current}>
            <div className="yat-top-card__background">{cardBg}</div>
            <div className="yat-top-card__details">
                <div className="yat-top-card__tag">{tagElement}</div>
                <div className="yat-top-card__copy">
                    <div className="yat-top-card__headline">{headline}</div>
                </div>
                <div className="yat-top-card__subheadline">{subHeadline}</div>
            </div>
            {!seeMore || <ArrowRightIcon className="yat-top-card__arrow" />}
        </div>
    );

    // TODO  - refactor the legacy tooltip
    if (bgImageClassName === 'yat-top-card-my-page-image') {
        //janky check, but we should get rid of this weird code anyway
        return <LegacyTooltipWrapper>{comp}</LegacyTooltipWrapper>;
    }

    return comp;
};

TopCard.defaultProps = {
    disabled: false,
    seeMore: false,
    size: 'single',
};
